import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  updateDeal,
  STATUS,
  getHoursForDate,
  isShopClosedOn,
  isWithinOpeningHours,
  getTokenBalance,
  setTokenBalance,
  consumeTokens,
  refreshTokenBalance,
} from '../../services/shopState';
import {
  getMyDeals,
  updateDeal as apiUpdateDeal,
  deleteDeal as apiDeleteDeal,
  getRetailerDealCoords,
} from '../../services/apiService';
import './ShopEditDealPage.css';
import { DEAL_CATEGORIES, normalizeCategory } from '../../constants/categories';

const isDemoMode = () => sessionStorage.getItem('walkin_demo_mode') === 'true';

// Single platform-wide list — the walker feed filters on exactly these.
const CATEGORIES = DEAL_CATEGORIES;

const STATUS_LABEL = {
  [STATUS.ACTIVE]:    '🟢 Actief',
  [STATUS.PAUSED]:    '⏸️ Gepauzeerd',
  [STATUS.EXPIRED]:   '⏱️ Verlopen',
  [STATUS.DELETED]:   '🗑️ Verwijderd',
  [STATUS.SCHEDULED]: '📅 Gepland',
};

const isoDate = (d) => d.toISOString().split('T')[0];
const todayIso = isoDate(new Date());

function readShopProfile() {
  try { return JSON.parse(sessionStorage.getItem('walkin_shop_profile') || '{}'); }
  catch { return {}; }
}

function dayDiff(a, b) {
  const x = new Date(a + 'T00:00:00');
  const y = new Date(b + 'T00:00:00');
  return Math.round((y - x) / 86400000) + 1;
}

const formatNlDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

const formatNlDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

function readDeal(id) {
  let all = [];
  try { all = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]'); }
  catch { all = []; }
  return all.find((d) => String(d.id) === String(id)) || null;
}

// Ownership for a deal that came out of the local store. The store is written
// per browser by this shop, so a deal only counts as someone else's when it
// actually names a different shop.
function isOwnedLocally(deal, shopName) {
  if (!deal) return false;
  const owner = deal.shopName || deal.createdBy;
  if (!owner || !shopName) return true;
  return owner === shopName;
}

export default function ShopEditDealPage() {
  // Re-render once the backend wallet has been fetched, so the extension
  // cost check uses the balance an admin may just have changed.
  const [, setWalletTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    refreshTokenBalance().then(() => { if (!cancelled) setWalletTick((t) => t + 1); });
    return () => { cancelled = true; };
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal] = useState(null);
  // Ownership is decided where the deal is loaded, not by comparing names
  // afterwards: in live mode the backend has already scoped the deal to the
  // authenticated retailer.
  const [owned, setOwned] = useState(false);
  const [loadingDeal, setLoadingDeal] = useState(true);
  const [coords, setCoords] = useState(null); // retailer lat/lng for live updates
  const profile = readShopProfile();
  const profileCategory = profile?.category ? normalizeCategory(profile.category) : '';

  // Load the deal to edit. Demo mode reads it from localStorage; live mode
  // pulls the retailer's own deals from the backend and resolves the
  // coordinates needed for later PUT updates.
  //
  // GET /deals/retailer/my-deals is scoped to the authenticated retailer by
  // the backend, so a deal found in that list belongs to this shop by
  // definition — and the response carries no shop name to compare against
  // anyway, which is why the old name check rejected every deal.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingDeal(true);
      if (isDemoMode()) {
        const local = readDeal(id);
        if (!cancelled) {
          setDeal(local);
          setOwned(isOwnedLocally(local, sessionStorage.getItem('walkin_shop_name')));
          setLoadingDeal(false);
        }
        return;
      }
      try {
        const [mine, resolved] = await Promise.all([
          getMyDeals(),
          getRetailerDealCoords().catch(() => null),
        ]);
        const found = (mine || []).find((d) => String(d.id) === String(id)) || null;
        if (!cancelled) {
          setDeal(found);
          setOwned(!!found); // scoped by the backend to this retailer
          if (resolved) {
            setCoords({
              lat: resolved.lat, lng: resolved.lng, areaId: resolved.areaId,
            });
          }
          setLoadingDeal(false);
        }
      } catch {
        // Offline fallback: the local store only holds this shop's own deals.
        const local = readDeal(id);
        if (!cancelled) {
          setDeal(local);
          setOwned(isOwnedLocally(local, sessionStorage.getItem('walkin_shop_name')));
          setLoadingDeal(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [emoji, setEmoji] = useState('🏷️');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [startDate, setStartDate] = useState(todayIso);
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState(todayIso);
  const [pausedToggle, setPausedToggle] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [toast, setToast] = useState('');
  const [originalEndDate, setOriginalEndDate] = useState(null);
  const [showTokenConfirmModal, setShowTokenConfirmModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);

  useEffect(() => {
    if (!deal) return;
    setTitle(deal.title || '');
    setDescription(deal.description || '');
    setEmoji(deal.emoji || '🏷️');
    // The deal's own category wins — it is what the retailer already chose for
    // this deal. The shop's branche only fills a gap.
    setCategory(deal.category ? normalizeCategory(deal.category) : (profileCategory || CATEGORIES[0]));
    setPausedToggle(deal.status === STATUS.PAUSED);
    // Pre-fill dates from validFrom + expiresAt
    if (deal.validFrom) {
      const vf = new Date(deal.validFrom);
      setStartDate(isoDate(vf));
      setStartTime(
        `${String(vf.getHours()).padStart(2, '0')}:${String(vf.getMinutes()).padStart(2, '0')}`
      );
    }
    if (deal.expiresAt) {
      const initialEnd = isoDate(new Date(deal.expiresAt));
      setEndDate(initialEnd);
      setOriginalEndDate(initialEnd);
    }
    // profileCategory is a plain string read from session storage, so it only
    // re-runs this when the shop's branche actually changes.
  }, [deal, profileCategory]);

  useEffect(() => {
    if (!toast) return;
    const id2 = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(id2);
  }, [toast]);

  // Locked only when the category came from the shop profile rather than from
  // the deal itself; a deal that carries its own category stays editable.
  const inheritedCategory = !!deal && !deal.category && !!profileCategory;

  // Schedule derived values — must be computed before any early return so
  // hook order stays consistent across renders.
  const startDateObj = useMemo(() => new Date(startDate + 'T00:00:00'), [startDate]);
  const endDateObj   = useMemo(() => new Date(endDate   + 'T00:00:00'), [endDate]);
  const durationDays = dayDiff(startDate, endDate);

  // Token-extension derived value also lives above the early return for the
  // same hook-order reason.
  const daysExtended = useMemo(() => {
    if (!originalEndDate) return 0;
    const a = new Date(originalEndDate + 'T00:00:00');
    const b = new Date(endDate + 'T00:00:00');
    const diff = Math.round((b - a) / 86400000);
    return diff > 0 ? diff : 0;
  }, [originalEndDate, endDate]);

  if (loadingDeal) {
    return (
      <div className="shop-edit">
        <header className="shop-edit__header">
          <button className="shop-edit__back" onClick={() => navigate(-1)} aria-label="Terug">←</button>
          <h1 className="shop-edit__title">Deal aanpassen</h1>
          <span className="shop-edit__spacer" />
        </header>
        <main className="shop-edit__main">
          <div className="shop-edit__missing">
            <div className="shop-edit__missing-icon">⏳</div>
            <h2>Deal laden…</h2>
          </div>
        </main>
      </div>
    );
  }

  if (!deal || !owned) {
    return (
      <div className="shop-edit">
        <header className="shop-edit__header">
          <button className="shop-edit__back" onClick={() => navigate(-1)} aria-label="Terug">←</button>
          <h1 className="shop-edit__title">Deal aanpassen</h1>
          <span className="shop-edit__spacer" />
        </header>
        <main className="shop-edit__main">
          <div className="shop-edit__missing">
            <div className="shop-edit__missing-icon">{deal && !owned ? '🚫' : '🤔'}</div>
            <h2>{deal && !owned ? 'Geen toegang' : 'Deal niet gevonden'}</h2>
            <p>
              {deal && !owned
                ? 'Je hebt geen toegang tot deze deal.'
                : 'Deze deal bestaat niet (meer) of is verwijderd.'}
            </p>
            <button
              className="shop-edit__primary"
              onClick={() => navigate('/shop/deals')}
            >
              Terug naar mijn deals →
            </button>
          </div>
        </main>
      </div>
    );
  }

  const isExpired = deal.status === STATUS.EXPIRED;

  const conversion =
    deal.views > 0 ? ((deal.redemptions || 0) / deal.views * 100).toFixed(1) : '0.0';

  const closedOnStart = isShopClosedOn(profile, startDateObj);
  const closedOnEnd   = isShopClosedOn(profile, endDateObj);
  const startHours    = getHoursForDate(profile, startDateObj);
  const startWithinHours = startHours && !startHours.closed
    ? isWithinOpeningHours(profile, startDateObj, startTime)
    : true;

  // Token-cost-for-extension: extending the end date charges 1 token per day.
  const balance = getTokenBalance();
  const isUnlimited = balance.unlimited;
  const tokensAvailable = isUnlimited ? 'unlimited' : balance.available;
  const endDateExtended = daysExtended > 0;
  const insufficientTokens =
    endDateExtended && !isUnlimited && Number(tokensAvailable) < daysExtended;

  const proceedWithSave = async () => {
    const validFromIso = new Date(`${startDate}T${startTime}:00`).toISOString();
    const expiresAtDate = new Date(`${endDate}T00:00:00`);
    expiresAtDate.setHours(23, 59, 59, 0);
    const isScheduled = new Date(validFromIso).getTime() > Date.now();

    // Decide new status. Pause toggle wins for active/paused; otherwise:
    // - scheduled if validFrom is in the future
    // - active if validFrom is now/past
    let nextStatus;
    if (deal.status === STATUS.ACTIVE || deal.status === STATUS.PAUSED) {
      nextStatus = pausedToggle ? STATUS.PAUSED : (isScheduled ? STATUS.SCHEDULED : STATUS.ACTIVE);
    } else {
      nextStatus = isScheduled ? STATUS.SCHEDULED : STATUS.ACTIVE;
    }

    if (isDemoMode()) {
      updateDeal(deal.id, {
        title: title.trim() || 'Naamloze deal',
        description, emoji: emoji || '🏷️', category,
        validFrom: validFromIso, expiresAt: expiresAtDate.toISOString(),
        startDate, startTime, endDate,
        validFromTime: startTime, validUntilTime: '23:59',
        scheduledFor: isScheduled ? validFromIso : null,
        status: nextStatus,
      });
      setToast('✅ Deal bijgewerkt!');
      setTimeout(() => navigate('/shop/deals'), 700);
      return true;
    }

    // Live mode: PUT the deal. The backend's update DTO mirrors create, so we
    // include the retailer's coordinates.
    try {
      await apiUpdateDeal(deal.id, {
        title: title.trim() || 'Naamloze deal',
        description, emoji: emoji || '🏷️', category,
        validFrom: validFromIso, expiresAt: expiresAtDate.toISOString(),
        validFromTime: `${startTime}:00`, validUntilTime: '23:59:00',
        // 'gepland' is derived from validFrom when the deal is read back and
        // deals.status does not accept it, so a scheduled deal is stored as
        // active and its future validFrom does the rest. Sending 'gepland'
        // made the whole extend request fail.
        status: nextStatus === STATUS.PAUSED ? STATUS.PAUSED : STATUS.ACTIVE,
        // Renewing a deal that was soft-deleted has to undo the delete too.
        active: true,
        lat: coords?.lat, lng: coords?.lng,
        areaId: coords?.areaId || undefined,
      });
      setToast('✅ Deal bijgewerkt!');
      setTimeout(() => navigate('/shop/deals'), 700);
      return true;
    } catch (e) {
      setToast(e?.message || 'Bijwerken mislukt.');
      // Reported rather than rethrown so the existing callers keep working;
      // deductTokensAndSave needs to know, because a failed save must not be
      // paid for.
      return false;
    }
  };

  const spentToast = () =>
    `✅ Deal verlengd! ${daysExtended} token${daysExtended > 1 ? 's' : ''} gebruikt.`;

  const deductTokensAndSave = async () => {
    // Demo mode keeps the browser-only wallet: there is no backend deal being
    // extended, so charging the real ledger would bill a retailer for a deal
    // that does not exist outside this tab.
    if (isDemoMode()) {
      if (!isUnlimited) {
        const newAvailable = Number(balance.available) - daysExtended;
        const newUsed = Number(balance.used) + daysExtended;
        setTokenBalance({ available: newAvailable, used: newUsed });
      }
      await proceedWithSave();
      setToast(spentToast());
      return;
    }

    // Live mode: save first, pay second — the order deal creation and
    // reactivation already use. It used to be the other way round, and
    // proceedWithSave was not awaited, so a failed save still deducted the
    // tokens and still showed the success toast.
    const saved = await proceedWithSave();
    if (!saved) return; // proceedWithSave has already reported why

    if (isUnlimited) {
      setToast('✅ Deal verlengd (Unlimited).');
      return;
    }

    let consumed;
    try {
      consumed = await consumeTokens(daysExtended);
    } catch (e) {
      // Not a balance problem — offline, session expired, server error. The
      // extension is saved either way, so say that rather than claiming it
      // was paid for.
      setToast(`Deal verlengd, maar afschrijven mislukte: ${e?.message || 'onbekende fout'}`);
      return;
    }

    if (!consumed) {
      setToast('Deal verlengd, maar er waren niet genoeg tokens om ervoor te betalen.');
      return;
    }
    setToast(spentToast());
  };

  const handleSave = () => {
    if (closedOnStart) return;
    if (!endDateExtended) {
      proceedWithSave();
      return;
    }
    if (isUnlimited) {
      proceedWithSave();
      return;
    }
    if (Number(tokensAvailable) < daysExtended) {
      setShowTokenModal(true);
      return;
    }
    setShowTokenConfirmModal(true);
  };

  const resetEndDateToOriginal = () => {
    if (originalEndDate) setEndDate(originalEndDate);
  };

  const handleDelete = async () => {
    // Removing a deal is final for the retailer: DELETE /deals/:id sets
    // active = false and every list hides it from then on.
    if (isDemoMode()) {
      updateDeal(deal.id, {
        status: STATUS.DELETED,
        deletedAt: new Date().toISOString(),
      });
      setShowDelete(false);
      navigate('/shop/deals?deleted=1');
      return;
    }
    // DELETE /deals/:id is the soft delete (it sets `active: false`). Patching
    // the status to 'verwijderd' was rejected — deals.status has no such value.
    try {
      await apiDeleteDeal(deal.id);
      setShowDelete(false);
      navigate('/shop/deals?deleted=1');
    } catch (e) {
      setToast(e?.message || 'Verwijderen mislukt.');
    }
  };

  return (
    <div className="shop-edit">
      <header className="shop-edit__header">
        <button className="shop-edit__back" onClick={() => navigate(-1)} aria-label="Terug">←</button>
        <h1 className="shop-edit__title">Deal aanpassen</h1>
        <button
          className="shop-edit__delete"
          onClick={() => setShowDelete(true)}
          aria-label="Verwijder deal"
          title="Verwijder deal"
        >
          🗑️
        </button>
      </header>

      <main className="shop-edit__main">

        {/* DEAL INFO */}
        <section className="shop-edit__card">
          <h2 className="shop-edit__card-title">Deal informatie</h2>

          <label className="shop-edit__field">
            <span className="shop-edit__label">Deal titel</span>
            <input
              className="shop-edit__input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </label>

          <label className="shop-edit__field">
            <span className="shop-edit__label">Beschrijving</span>
            <textarea
              className="shop-edit__textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <label className="shop-edit__field">
            <span className="shop-edit__label">Categorie</span>
            <select
              className="shop-edit__input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={inheritedCategory}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {inheritedCategory && (
              <small className="shop-edit__hint">Overgenomen uit je winkelprofiel</small>
            )}
          </label>
        </section>

        {/* GELDIGHEID */}
        <section className="shop-edit__card">
          <h2 className="shop-edit__card-title">Geldigheid</h2>

          <div className="shop-edit__row">
            <label className="shop-edit__field">
              <span className="shop-edit__label">Startdatum</span>
              <input
                className="shop-edit__input"
                type="date"
                value={startDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v);
                  if (v > endDate) setEndDate(v);
                }}
              />
            </label>
            <label className="shop-edit__field">
              <span className="shop-edit__label">Starttijd</span>
              <input
                className="shop-edit__input"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </label>
          </div>

          {/*
            No min/max on the end date: a date input narrows its own day
            segment to the allowed span, which left the field impossible to
            fill in by hand. The 7-day cap is enforced by the clamp effect
            above instead.

            A half-typed date reads back as '', and pushing that into state
            made React rewrite the input and wipe the digits the retailer had
            just typed — so only complete dates are taken.
          */}
          <label className="shop-edit__field">
            <span className="shop-edit__label">Einddatum</span>
            <input
              className="shop-edit__input"
              type="date"
              value={endDate}
              onChange={(e) => {
                if (e.target.value) setEndDate(e.target.value);
              }}
            />
          </label>

          {endDateExtended && (
            <div className="edit-deal__token-warning">
              <div className="edit-deal__token-warning-icon">🎫</div>
              <div className="edit-deal__token-warning-content">
                <div className="edit-deal__token-warning-title">
                  Verlenging kost tokens
                </div>
                <div className="edit-deal__token-warning-text">
                  Je verlengt deze deal met {daysExtended} dag{daysExtended > 1 ? 'en' : ''}.
                  Dit kost {daysExtended} token{daysExtended > 1 ? 's' : ''}.
                </div>
                <div className="edit-deal__token-balance">
                  Jouw saldo: {isUnlimited
                    ? '∞ Unlimited'
                    : `${tokensAvailable} tokens beschikbaar`}
                </div>
                {insufficientTokens && (
                  <div className="edit-deal__token-insufficient">
                    ⚠️ Niet genoeg tokens! Je hebt {tokensAvailable} token
                    {Number(tokensAvailable) !== 1 ? 's' : ''} maar {daysExtended} nodig.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="shop-edit__expiry">
            ⏰ Verloopt op {formatNlDate(new Date(`${endDate}T23:59:59`))} om 23:59
          </div>

          <div className="shop-edit__cost">
            {durationDays === 1
              ? `📅 Eén dag actief`
              : `📅 Meerdaagse deal: ${durationDays} dagen`}
          </div>

          {closedOnStart && (
            <div className="shop-edit__cost shop-edit__cost--block">
              ⚠️ Je winkel is gesloten op de startdatum. Kies een andere datum.
            </div>
          )}

          {!closedOnStart && closedOnEnd && (
            <div className="shop-edit__cost">
              ⚠️ Je winkel is gesloten op de einddatum.
            </div>
          )}

          {!closedOnStart && startHours && !startWithinHours && (
            <div className="shop-edit__cost">
              ⚠️ Starttijd {startTime} valt buiten je openingstijden
              ({startHours.open}–{startHours.close}).
            </div>
          )}
        </section>

        {/* STATUS */}
        <section className="shop-edit__card">
          <h2 className="shop-edit__card-title">Deal status</h2>
          <div className="shop-edit__status">
            <span className={`shop-edit__status-pill shop-edit__status-pill--${deal.status}`}>
              {STATUS_LABEL[deal.status] || deal.status}
            </span>
          </div>

          {!isExpired && (
            <label className="shop-edit__check">
              <input
                type="checkbox"
                checked={pausedToggle}
                onChange={(e) => setPausedToggle(e.target.checked)}
              />
              <span>
                {pausedToggle
                  ? 'Deal is gepauzeerd (uit op klantenfeed)'
                  : 'Deal is actief (zichtbaar op klantenfeed)'}
              </span>
            </label>
          )}

          {isExpired && (
            <p className="shop-edit__note">
              Deze deal is verlopen. Pas de start- en einddatum aan om hem
              weer in te plannen.
            </p>
          )}
        </section>

        {/* STATS */}
        <section className="shop-edit__card">
          <h2 className="shop-edit__card-title">Statistieken</h2>
          <div className="shop-edit__stats">
            <div className="shop-edit__stat">
              <span className="shop-edit__stat-label">Bekeken</span>
              <span className="shop-edit__stat-value">{deal.views || 0} keer</span>
            </div>
            <div className="shop-edit__stat">
              <span className="shop-edit__stat-label">Ingeleverd</span>
              <span className="shop-edit__stat-value">{deal.redemptions || 0} keer</span>
            </div>
            <div className="shop-edit__stat">
              <span className="shop-edit__stat-label">Conversie</span>
              <span className="shop-edit__stat-value">{conversion}%</span>
            </div>
            <div className="shop-edit__stat">
              <span className="shop-edit__stat-label">Aangemaakt op</span>
              <span className="shop-edit__stat-value">
                {formatNlDateTime(deal.createdAt)}
              </span>
            </div>
            <div className="shop-edit__stat shop-edit__stat--full">
              <span className="shop-edit__stat-label">Verloopt op</span>
              <span className="shop-edit__stat-value">
                {formatNlDate(deal.expiresAt)} 23:59
              </span>
            </div>
          </div>
        </section>

        <button
          className="shop-edit__primary"
          disabled={closedOnStart}
          onClick={handleSave}
        >
          Wijzigingen opslaan
        </button>
      </main>

      {toast && <div className="shop-edit__toast">{toast}</div>}

      {showTokenConfirmModal && (
        <div className="edit-deal__modal-overlay" role="dialog">
          <div className="edit-deal__modal">
            <div className="edit-deal__modal-icon">🎫</div>
            <h3>Tokens gebruiken?</h3>
            <p>Je verlengt deze deal met {daysExtended} dag{daysExtended > 1 ? 'en' : ''}.</p>

            <div className="edit-deal__modal-cost">
              <div className="edit-deal__modal-cost-row">
                <span>Kosten:</span>
                <span className="edit-deal__modal-cost-tokens">
                  {daysExtended} token{daysExtended > 1 ? 's' : ''}
                </span>
              </div>
              <div className="edit-deal__modal-cost-row">
                <span>Huidig saldo:</span>
                <span>{tokensAvailable} tokens</span>
              </div>
              <div className="edit-deal__modal-cost-row edit-deal__modal-cost-row--result">
                <span>Nieuw saldo:</span>
                <span className="edit-deal__modal-cost-new">
                  {Number(tokensAvailable) - daysExtended} tokens
                </span>
              </div>
            </div>

            <div className="edit-deal__modal-detail">
              <div>📅 Nieuwe einddatum:</div>
              <div className="edit-deal__modal-date">
                {new Date(endDate).toLocaleDateString('nl-NL', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                })}
              </div>
            </div>

            <button
              className="edit-deal__modal-btn-confirm"
              onClick={async () => {
                setShowTokenConfirmModal(false);
                await deductTokensAndSave();
              }}
            >
              ✅ Akkoord — tokens gebruiken
            </button>
            <button
              className="edit-deal__modal-btn-cancel"
              onClick={() => {
                setShowTokenConfirmModal(false);
                resetEndDateToOriginal();
              }}
            >
              Annuleren — datum niet wijzigen
            </button>
          </div>
        </div>
      )}

      {showTokenModal && (
        <div className="edit-deal__modal-overlay" role="dialog">
          <div className="edit-deal__modal">
            <div className="edit-deal__modal-icon">⚠️</div>
            <h3>Niet genoeg tokens</h3>
            <p>
              Je hebt {tokensAvailable} token{Number(tokensAvailable) !== 1 ? 's' : ''}
              {' '}maar hebt {daysExtended} nodig voor deze verlenging.
            </p>

            <div className="edit-deal__modal-cost">
              <div className="edit-deal__modal-cost-row">
                <span>Nodig:</span>
                <span className="edit-deal__modal-cost-tokens">{daysExtended} tokens</span>
              </div>
              <div className="edit-deal__modal-cost-row">
                <span>Beschikbaar:</span>
                <span style={{ color: '#E8621A' }}>{tokensAvailable} tokens</span>
              </div>
              <div className="edit-deal__modal-cost-row">
                <span>Tekort:</span>
                <span style={{ color: '#FF3B30', fontWeight: 600 }}>
                  {daysExtended - Number(tokensAvailable)} tokens
                </span>
              </div>
            </div>

            <p style={{ fontSize: 13, color: '#6B7280', textAlign: 'center' }}>
              Koop meer tokens om deze deal te verlengen.
            </p>

            <button
              className="edit-deal__modal-btn-confirm"
              onClick={() => {
                setShowTokenModal(false);
                navigate('/shop/tokens');
              }}
            >
              🎫 Tokens kopen →
            </button>
            <button
              className="edit-deal__modal-btn-secondary"
              onClick={() => {
                setShowTokenModal(false);
                resetEndDateToOriginal();
              }}
            >
              Terug — datum niet wijzigen
            </button>
            <button
              className="edit-deal__modal-btn-cancel"
              onClick={() => setShowTokenModal(false)}
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="shop-edit__modal" role="dialog">
          <div className="shop-edit__modal-card">
            <div className="shop-edit__modal-icon">🗑️</div>
            <h3>Deal verwijderen?</h3>
            <p>
              De deal verdwijnt direct uit de app en uit je overzicht. Dit kan
              niet ongedaan worden gemaakt. Gebruikte tokens worden niet
              teruggestort.
            </p>
            <button
              className="shop-edit__modal-danger"
              onClick={handleDelete}
            >
              Verwijder deal
            </button>
            <button
              className="shop-edit__modal-cancel"
              onClick={() => setShowDelete(false)}
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
